import { Button, Icon, Sparkline } from '@bloo-team/ui'
import { Copy, Search, Star } from '@bloo-team/ui/icons'
import { SlideOver } from '@bloo-team/ui'
import { HISTORY_DAYS } from '../../lib/signal-history'
import type { SignalEntity } from '../../lib/signal-model'
import { agoLabel } from './SignalPanel'
import type { EntityFlyoutProps } from './types'

/**
 * Entity flyout (docs/signals-requirements.md §5, decision S15): the 30-day
 * story of one host / user / ip — history sparkline, first/last seen, top
 * detections, prevalence, cases, watchlist. Stacks as a second SlideOver
 * over the signal panel (react-aria ModalOverlay nests; Escape closes the
 * top one). Props-in/callbacks-out; every number arrives computed.
 */

function quote(v: string): string {
  return `'${v.replace(/'/g, "''")}'`
}

/** `stream=SIGNALS where suspectuser = 'x' | duration 30d | limit 500` — the flyout's wider pivot. */
export function entityHistoryQuery(e: SignalEntity): string {
  return `stream=SIGNALS where ${e.role.toLowerCase()}${e.field.toLowerCase()} = ${quote(e.value)} | duration ${HISTORY_DAYS}d | limit 500`
}

function copyText(text: string) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(text).catch(() => {})
  }
}

function shortDate(at: number): string {
  const d = new Date(at)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export function EntityFlyout({
  entity,
  history,
  prevalence,
  cases,
  suppressions,
  watched,
  open,
  recent,
  onClose,
  onToggleWatch,
  onOpenSearch,
  onOpenAsHuntFromEntity,
  onSelectSignal,
}: EntityFlyoutProps) {
  const field = entity.field.toLowerCase()
  const prevalenceText =
    prevalence.total === 0
      ? 'no hosts in baseline'
      : `seen on ${prevalence.hosts} of ${prevalence.total} host${prevalence.total === 1 ? '' : 's'}${
          prevalence.rare === true ? ' (rare)' : prevalence.rare === false ? ' (common)' : ''
        }`

  const actions = (
    <>
      <Button variant="outline" size="sm" tone="neutral" onPress={() => onOpenSearch(entityHistoryQuery(entity))}>
        <Icon icon={Search} size={12} /> Search events
      </Button>
      {onOpenAsHuntFromEntity ? (
        <Button variant="outline" size="sm" tone="neutral" onPress={onOpenAsHuntFromEntity}>
          Open as hunt
        </Button>
      ) : null}
      <Button variant="ghost" size="sm" tone="neutral" onPress={() => copyText(entity.value)}>
        <Icon icon={Copy} size={12} /> Copy
      </Button>
    </>
  )

  return (
    <SlideOver open={open} onClose={onClose} eyebrow={`entity · ${entity.role.toLowerCase()} ${field}`} title={entity.value} actions={actions}>
      <div className="ef-body">
        <div className="hud-row hud-row-between ef-watch-row">
          <span className="hud-unit">{HISTORY_DAYS}-day baseline</span>
          <button
            type="button"
            className="list-chip ef-watch"
            aria-pressed={watched}
            aria-label={watched ? 'Unwatch entity' : 'Watch entity'}
            onClick={onToggleWatch}
          >
            <Icon icon={Star} size={11} /> {watched ? 'watching' : 'watch'}
          </button>
        </div>

        <dl className="ef-stats" aria-label="entity stats">
          <div>
            <dt>signals 30d</dt>
            <dd className="num">{history.count30d}</dd>
          </div>
          <div>
            <dt>first seen</dt>
            <dd>{history.first === null ? '—' : agoLabel(history.first)}</dd>
          </div>
          <div>
            <dt>last seen</dt>
            <dd>{history.last === null ? '—' : agoLabel(history.last)}</dd>
          </div>
          <div>
            <dt>hosts seen with</dt>
            <dd className="num">{history.hostsSeenWith.length}</dd>
          </div>
          <div className="ef-stat-wide">
            <dt>prevalence</dt>
            <dd data-rare={prevalence.rare === true ? '' : undefined}>{prevalenceText}</dd>
          </div>
        </dl>

        <section className="sp-section">
          <div className="hud-row hud-row-between sp-section-head">
            <span className="hud-eyebrow">signals per day</span>
          </div>
          <div className="ef-spark" role="img" aria-label={`${history.count30d} signals over ${HISTORY_DAYS} days`}>
            <Sparkline data={history.daily} width={360} height={48} dot={false} />
          </div>
        </section>

        <section className="sp-section">
          <div className="hud-row hud-row-between sp-section-head">
            <span className="hud-eyebrow">top detections · {history.detections.length}</span>
          </div>
          {history.detections.length === 0 ? (
            <p className="q-facet-note">No signals for this entity in the baseline.</p>
          ) : (
            <ul className="ef-list" aria-label="top detections">
              {history.detections.map((d) => (
                <li key={d.name} className="hud-row hud-row-between">
                  <span className="ef-list-name">{d.name}</span>
                  <span className="num mono">{d.count}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {history.hostsSeenWith.length ? (
          <section className="sp-section">
            <div className="hud-row hud-row-between sp-section-head">
              <span className="hud-eyebrow">hosts seen with · {history.hostsSeenWith.length}</span>
            </div>
            <p className="mono ef-hosts">{history.hostsSeenWith.slice(0, 12).join(' · ')}{history.hostsSeenWith.length > 12 ? ' …' : ''}</p>
          </section>
        ) : null}

        <section className="sp-section">
          <div className="hud-row hud-row-between sp-section-head">
            <span className="hud-eyebrow">recent signals · {recent.length}</span>
          </div>
          {recent.length === 0 ? (
            <p className="q-facet-note">None.</p>
          ) : (
            <ol className="sp-timeline" aria-label="recent signals">
              {recent.map((s) => (
                <li key={s.uuid}>
                  <button type="button" className="sp-tl-row" onClick={() => onSelectSignal(s.uuid)}>
                    <span className="sp-tl-time">{shortDate(s.at)}</span>
                    <span className="sp-sev" data-severity={s.severity} />
                    <span className="sp-tl-name">{s.detectionName || '—'}</span>
                    <span className="sp-tl-ents">{s.entities.map((e) => e.value).join(' · ')}</span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="sp-section">
          <div className="hud-row hud-row-between sp-section-head">
            <span className="hud-eyebrow">cases · {cases.length}</span>
          </div>
          {cases.length === 0 ? (
            <p className="q-facet-note">No case holds a signal with this entity.</p>
          ) : (
            <ul className="ef-list" aria-label="cases">
              {cases.map((c) => (
                <li key={c.id} className="hud-row">
                  <span className="mono">{c.number}</span>
                  <span className="ef-list-name">{c.title}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="sp-section">
          <div className="hud-row hud-row-between sp-section-head">
            <span className="hud-eyebrow">suppression rules · {suppressions.length}</span>
          </div>
          {suppressions.length === 0 ? (
            <p className="q-facet-note">No rule matches this entity.</p>
          ) : (
            <ul className="sp-rules" aria-label="matching suppression rules">
              {suppressions.map((r) => (
                <li key={r.id} className="mono">{r.summary}</li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </SlideOver>
  )
}
